import { AuthConfig } from '../types/AuthConfig';
import { useConsentStore } from '../services/state/consentStore';

const analyticsAllowed = () => useConsentStore.getState().has('analytics');

// Of the query, only GA4's campaign params go to GA, and only in the customer tag's first page_view
// (the hit GA4 attributes a session from); never the hash. Landings carry reset/invite keys,
// emails, OAuth codes and Stripe secrets there, legacy #/ links too.
const campaignParams = new Set([
  'utm_id',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_source_platform',
  'utm_creative_format',
  'utm_marketing_tactic',
]);

const campaignQuery = () => {
  const params = Array.from(new URLSearchParams(window.location.search)).filter(([name]) =>
    campaignParams.has(name),
  );
  return params.length ? `?${new URLSearchParams(params).toString()}` : '';
};

const pageLocation = (campaign = '') =>
  `${window.location.origin}${window.location.pathname}${campaign}`;

// Other params the customer tag may load next to (gtag.js copies them into its own hits): ones our
// emailed links use, with nothing sensitive. Anything added here reaches every customer's GA.
const gtagSafeParams = new Set(['comment', 'activeTab']);

const urlIsSafeForGtag = () =>
  !window.location.hash &&
  Array.from(new URLSearchParams(window.location.search).keys()).every(
    (name) => campaignParams.has(name) || gtagSafeParams.has(name),
  );

let initializedMeasurementId: string | null = null;

// Campaign params from a URL the tag had to wait out, sent with its first page_view.
let deferredCampaign = '';

// App calls this while the customer tag can't load (signed out, no analytics consent), so a campaign
// held from an earlier landing isn't sent with a later session's first page_view.
export const forgetDeferredCampaign = () => {
  deferredCampaign = '';
};

// Google's documented kill switch: unlike gtag consent signals, it stops the
// tag entirely (including cookieless pings), and flipping it back resumes
// collection without re-running config.
const setGADisabled = (measurementId: string, disabled: boolean) => {
  (window as any)[`ga-disable-${measurementId}`] = disabled;
};

// Disables/re-enables the initialized customer tag when the user changes the
// analytics consent category mid-session (the consent banner syncs without a
// reload, so a loaded tag would otherwise keep collecting after a revoke).
export const updateCustomerGAConsent = (allowed: boolean) => {
  if (initializedMeasurementId) {
    setGADisabled(initializedMeasurementId, !allowed);
  }
};

// Initializes the customer's own Google Analytics property (company.google_analytics)
// via gtag.js. gtag accepts both GA4 (G-) and legacy (UA-) ids, so there is no
// format branching. send_page_view is disabled — trackPageView drives all pageviews
// so SPA route changes and the initial load are counted exactly once.
export const initCustomerGA = (measurementId?: string) => {
  if (!measurementId || measurementId === initializedMeasurementId || !analyticsAllowed()) {
    return;
  }
  // Wait for a URL with nothing to leak: once loaded, gtag.js reads document.location itself
  // (enhanced measurement's history page_views and their page_referrer, a bundled destination's
  // own load-time page_view), and page_location settings don't reach those hits. App retries on
  // every navigation.
  if (!urlIsSafeForGtag()) {
    deferredCampaign = deferredCampaign || campaignQuery();
    return;
  }
  if (initializedMeasurementId) {
    // The id changed mid-session (admin edit reaches us via the /me refetch);
    // a loaded tag can't be unloaded, so silence it before configuring the new one.
    setGADisabled(initializedMeasurementId, true);
  }
  initializedMeasurementId = measurementId;
  setGADisabled(measurementId, false);

  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, { send_page_view: false });
  // send_to keeps the initial hit off Continu's own tags, which were configured
  // earlier (index.html) and already saw this page via trackPageView on mount.
  window.gtag('event', 'page_view', {
    send_to: measurementId,
    page_path: window.location.pathname,
    page_location: pageLocation(campaignQuery() || deferredCampaign),
    page_title: document.title,
  });
  deferredCampaign = '';
};

export const trackPageView = (path: string) => {
  try {
    if (analyticsAllowed() && window.gtag) {
      window.gtag('event', 'page_view', {
        page_path: path,
        page_location: pageLocation(),
        page_title: document.title,
      });
    }
  } catch (e) {
    console.error(e);
  }
};

export const setUserInfo = (authConfig: AuthConfig) => {
  try {
    const { user, company } = authConfig;

    if (analyticsAllowed()) {
      if (window.plantrack) {
        window.plantrack.identify(user._id, {
          email: user.email,
          name: user.first_name + ' ' + user.last_name,
          companyId: company._id,
        });
      }
      if (window.gtag) {
        window.gtag('set', { user_id: user.email });
      }
    }
  } catch (e) {
    console.error(e);
  }
};

export const trackEvent = (event: any, attributes?: any) => {
  try {
    if (analyticsAllowed() && window.plantrack) {
      window.plantrack.track(event);
    }
    // New Relic is strictly-necessary error/reliability monitoring — never gated.
    if (window.newrelic) {
      window.newrelic.addPageAction(event, attributes);
    }
  } catch (e) {
    console.error(e);
  }
};
